import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { User, Phone, Mail, Store, MapPin, Lock, Save, Eye, EyeOff, Check } from 'lucide-react';

const Profile = () => {
  const [profile, setProfile] = useState({});
  const [form, setForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [tab, setTab] = useState('profile');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/profile');
      setProfile(res.data);
      setForm(res.data);
    } catch (err) {}
    setLoading(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/profile', form);
      setSaved(true);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed');
    }
    setSaving(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/profile/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Password changed successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Password change failed');
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-4 md:space-y-6 max-w-2xl mx-auto">
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-white">Profile Settings</h2>
        <p className="text-gray-400 text-sm mt-1">Manage your account details</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-[#0d1410] border border-white/5 rounded-xl p-1">
        <button onClick={() => setTab('profile')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === 'profile' ? 'bg-emerald-500/20 text-emerald-400' : 'text-gray-400 hover:text-white'}`}>
          Profile Info
        </button>
        <button onClick={() => setTab('password')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === 'password' ? 'bg-emerald-500/20 text-emerald-400' : 'text-gray-400 hover:text-white'}`}>
          Change Password
        </button>
      </div>

      {/* Messages */}
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>}
      {success && <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-xl text-sm flex items-center gap-2"><Check size={16} /> {success}</div>}

      {/* Profile Tab */}
      {tab === 'profile' && (
        <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-6">
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Full Name</label>
              <div className="relative">
                <User size={18} className="absolute left-4 top-3.5 text-gray-500" />
                <input type="text" value={form.fullName || ''} onChange={e => setForm({...form, fullName: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Phone</label>
              <div className="relative">
                <Phone size={18} className="absolute left-4 top-3.5 text-gray-500" />
                <input type="text" value={form.phone || ''} onChange={e => setForm({...form, phone: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-3.5 text-gray-500" />
                <input type="email" value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Shop Name</label>
              <div className="relative">
                <Store size={18} className="absolute left-4 top-3.5 text-gray-500" />
                <input type="text" value={form.shopName || ''} onChange={e => setForm({...form, shopName: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Location</label>
              <div className="relative">
                <MapPin size={18} className="absolute left-4 top-3.5 text-gray-500" />
                <input type="text" value={form.location || ''} onChange={e => setForm({...form, location: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" />
              </div>
            </div>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition text-sm disabled:opacity-50">
              {saved ? <Check size={18} /> : <Save size={18} />}
              {saved ? 'Saved!' : 'Update Profile'}
            </button>
          </form>
        </div>
      )}

      {/* Password Tab */}
      {tab === 'password' && (
        <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-6">
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Current Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-3.5 text-gray-500" />
                <input type={showCurrent ? 'text' : 'password'} value={passwordForm.currentPassword}
                  onChange={e => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                  className="w-full pl-12 pr-12 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" required />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-4 top-3.5 text-gray-500">
                  {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">New Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-3.5 text-gray-500" />
                <input type={showNew ? 'text' : 'password'} value={passwordForm.newPassword}
                  onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  className="w-full pl-12 pr-12 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" required />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-4 top-3.5 text-gray-500">
                  {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Confirm New Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-3.5 text-gray-500" />
                <input type="password" value={passwordForm.confirmPassword}
                  onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 bg-[#0d1611] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" required />
              </div>
            </div>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition text-sm disabled:opacity-50">
              <Lock size={18} /> Change Password
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Profile;
